import { ListOutreachConfig } from "@/pages/Private/pages/List/schema/list-outreach-config.schema";

import { FrameworkOverrides } from "./types";

/**
 * "No language forced" — the id of `LanguageSelectDropdown`'s first option and the value
 * `GenerateBar` starts on. The API spells the same sentinel `AUTO_GENERATION_LANGUAGE_AUTO`
 * (`apps/api/src/list/outreach/auto-generation-dto.ts`), so a null `chatLanguage` is a no-op on
 * both paths rather than a special case.
 */
export const GENERATE_BAR_LANGUAGE_AUTO = "auto";

/** The part of a list's outreach config the manual bar seeds itself from. */
export type GenerateBarPrefillConfig = Pick<
	ListOutreachConfig,
	"autoCreateAiCopy" | "chatLanguage" | "chatFrameworkId"
>;

export interface GenerateBarPrefill {
	/** What the language control opens on. */
	language: string;
	/**
	 * What to write into the orchestration slice's framework overrides, or `null` to leave the
	 * slice untouched. `null` is deliberately **not** "three null steps": that would be an explicit
	 * instruction to clear a choice, where Auto-suggest simply has nothing to say.
	 */
	frameworkOverrides: FrameworkOverrides | null;
}

/**
 * What a manual generation for a lead on this list should open with (PREC-3687 D8 / AC 18).
 *
 * Pure input → output. `null` means "seed nothing, today's defaults stand", for either of two
 * reasons:
 *
 * 1. There is no config to read — no list, or it has not been fetched yet.
 * 2. **The list's *Always create AI copy* toggle is off.** With the toggle off the Chat section
 *    renders Language and Copywriting framework greyed out and non-interactive (AC 17), so seeding
 *    a manual draft from them contradicted what the step showed the user: a list could be sitting
 *    on a visibly-disabled `German` and still write every hand-requested draft in German, with no
 *    control anywhere that explained why. The toggle is the switch for the list's chat settings as
 *    a whole; when it is off they are inert, on the manual path as well as the automatic one.
 *
 * The values are kept either way (AC 17 is about not clearing them), so flipping the toggle back on
 * restores the prefill along with everything else.
 */
export const resolveGenerateBarPrefill = (
	config: GenerateBarPrefillConfig | undefined
): GenerateBarPrefill | null => {
	if (!config || !config.autoCreateAiCopy) {
		return null;
	}

	return {
		language: config.chatLanguage ?? GENERATE_BAR_LANGUAGE_AUTO,
		frameworkOverrides: config.chatFrameworkId
			? frameworkOverridesFor(config.chatFrameworkId)
			: null,
	};
};

/**
 * An explicit framework on the list "forces one shape across the list" (§4.3), so it applies to
 * every step. Mirrors `frameworkOverridesFor` in `apps/api/src/list/outreach/auto-generation-dto.ts`
 * — a manually generated draft and an auto-generated one must not disagree about what the list
 * setting means.
 */
const frameworkOverridesFor = (frameworkId: number): FrameworkOverrides => ({
	step1FrameworkId: frameworkId,
	step2FrameworkId: frameworkId,
	step3FrameworkId: frameworkId,
});
